import {
  Heading,
  HStack,
  Text,
  VStack,
  Image,
  Square,
  List,
  ListIcon,
  ListItem,
  Box,
  Button,
  IconButton,
  Flex,
  Spinner
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { MdDoneAll } from 'react-icons/md'
import { use100vh } from 'react-div-100vh'
import moment from 'moment'
import { money } from '../../../utils/formatter'
import { Axios } from '../../../utils/axios'
import { handleAxiosError } from '../../../utils/error'
import { Error404 } from '../../errors/404'
import useMotorStore from '../../../state/Motor.store'
import { IPolicy } from '../../../types'

type Props = {
  setAmount: (amount?: number) => void
  setScreen: (screen: string) => void
}

const QuoteDetail = ({ setScreen, setAmount }: Props) => {
  const user_id = JSON.parse(localStorage.getItem('auth') || '')?.user?.id
  const history = useHistory()
  const height = use100vh()
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)
  const [quote, setQuote] = useState<IPolicy>()

  const [generateAccount, isLoading] = useMotorStore((state) => [
    state.generateAccount,
    state.loading
  ])

  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await Axios.get(`/policy?id=${id}`)
        setLoading(false)
        setQuote(data.data.policies[0])
        console.log(data.data.policies[0])
      } catch (error: any) {
        setLoading(false)
        handleAxiosError(error)
      }
    })()
  }, [id])

  if (loading) {
    return (
      <VStack h="100vh" justify="center" flex={1}>
        <Spinner size="xl" />
      </VStack>
    )
  }

  const onboardBuyPolicy = async () => {
    try {
      await generateAccount(id, String(user_id))
      setScreen('payment')
      setAmount(quote?.amount)
    } catch (error) {
      console.log(error)
    }
  }

  if (!quote) {
    return <Error404 />
  }

  return (
    <Flex
      pos="relative"
      flexDir="column"
      maxW={{ md: '480px' }}
      w="full"
      mx="auto"
      pb="2.4rem"
      px="2.4rem"
      pt="3.2rem"
      h={height || undefined}>
      <Box flex={1}>
        <HStack cursor="pointer" onClick={() => history.goBack()} mb="1rem" pb="2.4rem">
          <IconButton
            h="auto"
            justifyContent="flex-start"
            color="transparent"
            bg="transparent"
            w="30px"
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            _focus={{ bg: 'transparent' }}
            aria-label="Search database"
            icon={
              <svg
                width="15"
                height="14"
                viewBox="0 0 15 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M3.4451 6.10003H14.3996V7.89997H3.4451L8.27256 12.7274L7 14L0 7L7 0L8.27256 1.27256L3.4451 6.10003Z"
                  fill="#2E279D"
                />
              </svg>
            }
          />
          <Heading
            ml="-30px !important"
            textAlign="center"
            fontWeight="600"
            flex="1"
            fontSize="1.4rem"
            color="black">
            {`${quote.policy_type === 'gadget' ? 'Gadget' : 'Motor'}`} Premium
          </Heading>
        </HStack>

        <VStack
          boxShadow="0px 4px 12px rgba(154, 160, 166, 0.04)"
          p="1.6rem"
          bg="white"
          borderRadius="1.2rem"
          align="flex-start">
          <HStack
            pb="2.4rem"
            borderBottom="1px solid #F7F7F7"
            spacing="1.6rem"
            w="full"
            align="flex-start">
            <VStack spacing="1.6rem" align="flex-start" flex={1}>
              <HStack spacing="1.6rem">
                <Square borderRadius="0.8rem" p="0.4rem" bg="#F7F7F7">
                  <Image
                    boxSize="35px"
                    src={`/images/buy-${quote.policy_type === 'vehicle' ? 'car' : 'gadget'}.png`}
                  />
                </Square>
                <VStack spacing={0} align="flex-start">
                  <Text fontSize="1rem">Insured Item</Text>
                  <Text color="black" fontWeight="500" fontSize="1.2rem">
                    {quote[quote.policy_type].make
                      ? `${quote[quote.policy_type].make} / ${quote[quote.policy_type].model}`
                      : `${quote[quote.policy_type].model}`}
                  </Text>
                </VStack>
              </HStack>
              <HStack w="full" justify="space-between">
                <VStack
                  ml={quote.policy_type === 'vehicle' ? 0 : '5.9rem !important'}
                  spacing={0}
                  align="flex-start">
                  <Text fontSize="1rem">Policy Number</Text>
                  <Text color="black" fontWeight="500" fontSize="1.2rem">
                    {quote.policy_number || '-'}
                  </Text>
                </VStack>
                {quote.vehicle && (
                  <VStack spacing={0} align="flex-start">
                    <Text fontSize="1rem">Vehicle Plate Number</Text>
                    <Text color="black" fontWeight="500" fontSize="1.2rem">
                      {quote.vehicle.plate_number}
                    </Text>
                  </VStack>
                )}
                <VStack spacing={0} align="flex-start">
                  <Text fontSize="1rem">Valid Till</Text>
                  <Text color="black" fontWeight="500" fontSize="1.2rem">
                    {moment(quote.expiration_date).format('DD-MM-YY')}
                  </Text>
                </VStack>
              </HStack>
            </VStack>
          </HStack>

          <Heading mt="2.4rem !important" color="brand.blue" fontSize="1.2rem" fontWeight="400">
            What’s covered by this Policy?
          </Heading>

          <List mt="1.2rem !important" fontSize="1rem" spacing="1.5rem">
            <ListItem d="flex">
              <ListIcon mt="0.3rem" as={MdDoneAll} color="green.500" />
              {quote.policy_type === 'vehicle'
                ? 'Kappa provides Third Party Motor insurance for both private and commercial vehicles. '
                : ' Covers the cost of repairs in the event of accidental damage.'}
            </ListItem>
            <ListItem d="flex">
              <ListIcon mt="0.3rem" as={MdDoneAll} color="green.500" />
              {quote.policy_type === 'vehicle'
                ? ' It covers the policyholder against legal liability to third parties in the event of bodily damage or death.'
                : 'Cost of replacement in cases of loss or theft.'}
            </ListItem>
            <ListItem d="flex">
              <ListIcon mt="0.3rem" as={MdDoneAll} color="green.500" />
              {quote.policy_type === 'vehicle'
                ? 'It also covers property damage involving the insured vehicle.'
                : 'Protects you against the usual risks attached to owning a gadget.'}
            </ListItem>
          </List>
        </VStack>
        <HStack
          mt="2.4rem"
          w="100%"
          border="1px dashed #0AB86F"
          justify="space-between"
          bg="rgba(223, 246, 240, 0.2)"
          py="1.3rem"
          mb="2rem"
          borderRadius="1.2rem"
          px="3rem">
          <HStack>
            <Image mr="1.2rem" src="/images/shield.svg" />
            <Text color="brand.blue">
              Third Party
              <br /> Policy
            </Text>
          </HStack>
          <VStack align="flex-start">
            <Heading color="brand.blue">{money(quote.amount)}</Heading>
            <Text fontSize="1rem" color="black">
              per {quote.product.period}
            </Text>
          </VStack>
        </HStack>
      </Box>
      <Button isLoading={isLoading} onClick={onboardBuyPolicy} isFullWidth variant="green">
        Buy This Policy
      </Button>
    </Flex>
  )
}

export { QuoteDetail }
